import React, { useState } from "react";
import { toast } from "react-toastify";
import ButtonWithIcon from "../../components/ButtonwithIcon/ButtonWithIcon";
import "./LeadQuickAddModal.scss";

const LeadQuickAddModal = ({
  isOpen,
  onClose,
  onLeadCreated,
  axiosInstance,
  getApiEndpoint,
}) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    leadSource: "CAMPAIGN",
    leadStatus: "AVAILABLE",
    email: "",
    phoneNumber: "",
    companyName: "",
    jobTitle: "",
    industry: "",
    website: "",
    notes: "",
    tags: "",
    leadScore: 0,
    assignedToId: 0,
    category: "CASH",
    contactPerson: [
      {
        contactPersonName: "",
        email: "",
        whatsAppNumber: "",
        mobileNumber: "",
      },
    ],
  });

  const leadSources = ["CAMPAIGN", "REFERRAL", "INBOUND", "OUTBOUND", "OTHER"];
  const categories = ["CASH", "CREDIT"];

  const formatText = (text) => {
    if (!text) return "";
    return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.companyName || !formData.email) {
      toast.error("Company name and email are required");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        ...formData,
        leadStatus: "AVAILABLE",
        assignedToId: 0,
      };

      const response = await axiosInstance.post(
        getApiEndpoint("/leads"),
        payload,
      );

      if (response.status === 200 || response.status === 201) {
        toast.success("Lead created successfully!");
        const createdLead = response.data.data;
        if (onLeadCreated) {
          onLeadCreated(createdLead);
        }
        resetForm();
        onClose();
      }
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      leadSource: "CAMPAIGN",
      leadStatus: "AVAILABLE",
      email: "",
      phoneNumber: "",
      companyName: "",
      jobTitle: "",
      industry: "",
      website: "",
      notes: "",
      tags: "",
      leadScore: 0,
      assignedToId: 0,
      category: "CASH",
      contactPerson: [
        {
          contactPersonName: "",
          email: "",
          whatsAppNumber: "",
          mobileNumber: "",
        },
      ],
    });
  };

  const addContactPerson = () => {
    setFormData((prev) => ({
      ...prev,
      contactPerson: [
        ...prev.contactPerson,
        {
          contactPersonName: "",
          email: "",
          whatsAppNumber: "",
          mobileNumber: "",
        },
      ],
    }));
  };

  const removeContactPerson = (index) => {
    if (formData.contactPerson.length === 1) {
      toast.info("At least one contact person is required");
      return;
    }
    setFormData((prev) => ({
      ...prev,
      contactPerson: prev.contactPerson.filter((_, i) => i !== index),
    }));
  };

  const updateContactPerson = (index, field, value) => {
    const newContacts = [...formData.contactPerson];
    newContacts[index][field] = value;
    setFormData((prev) => ({
      ...prev,
      contactPerson: newContacts,
    }));
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal-content quick-add-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header">
          <h3>Quick Add Lead</h3>
          <button className="close-btn" onClick={onClose}>
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit} className="quick-add-form">
          {/* Basic Information */}
          <div className="form-section">
            <h4>Basic Information</h4>
            <div className="form-row">
              <div className="form-group">
                <label>Company Name *</label>
                <input
                  type="text"
                  name="companyName"
                  value={formData.companyName}
                  onChange={handleInputChange}
                  required
                  placeholder="Enter company name"
                />
              </div>

              <div className="form-group">
                <label>Email *</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  placeholder="company@example.com"
                />
              </div>

              <div className="form-group">
                <label>Phone</label>
                <input
                  type="tel"
                  name="phoneNumber"
                  value={formData.phoneNumber}
                  onChange={handleInputChange}
                  placeholder="+1234567890"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Job Title</label>
                <input
                  type="text"
                  name="jobTitle"
                  value={formData.jobTitle}
                  onChange={handleInputChange}
                  placeholder="e.g., CEO, Manager"
                />
              </div>

              <div className="form-group">
                <label>Industry</label>
                <input
                  type="text"
                  name="industry"
                  value={formData.industry}
                  onChange={handleInputChange}
                  placeholder="e.g., Technology"
                />
              </div>

              <div className="form-group">
                <label>Website</label>
                <input
                  type="url"
                  name="website"
                  value={formData.website}
                  onChange={handleInputChange}
                  placeholder="https://example.com"
                />
              </div>
            </div>
          </div>

          {/* Lead Classification */}
          <div className="form-section">
            <h4>Lead Classification</h4>
            <div className="form-row">
              <div className="form-group">
                <label>Category *</label>
                <div className="button-group">
                  {categories.map((cat) => (
                    <button
                      key={cat}
                      type="button"
                      className={`btn-option ${
                        formData.category === cat ? "active" : ""
                      }`}
                      onClick={() =>
                        setFormData((prev) => ({ ...prev, category: cat }))
                      }
                    >
                      {formatText(cat)}
                    </button>
                  ))}
                </div>
              </div>

              <div className="form-group">
                <label>Source *</label>
                <div className="button-group">
                  {leadSources.map((source) => (
                    <button
                      key={source}
                      type="button"
                      className={`btn-option ${
                        formData.leadSource === source ? "active" : ""
                      }`}
                      onClick={() =>
                        setFormData((prev) => ({ ...prev, leadSource: source }))
                      }
                    >
                      {formatText(source)}
                    </button>
                  ))}
                </div>
              </div>

              <div className="form-group">
                <label>Lead Score</label>
                <div className="button-group">
                  {Array.from({ length: 5 }, (_, i) => i + 1).map((score) => (
                    <button
                      key={score}
                      type="button"
                      className={`btn-option score-btn ${
                        formData.leadScore === score ? "active" : ""
                      }`}
                      onClick={() =>
                        setFormData((prev) => ({ ...prev, leadScore: score }))
                      }
                    >
                      {"★".repeat(score)}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Additional Details */}
          <div className="form-section">
            <h4>Additional Details</h4>
            <div className="form-row two-columns">
              <div className="form-group">
                <label>Tags</label>
                <textarea
                  name="tags"
                  value={formData.tags}
                  onChange={handleInputChange}
                  rows="2"
                  placeholder="Comma-separated tags (e.g., VIP, Urgent, Tech)"
                />
              </div>

              <div className="form-group">
                <label>Notes</label>
                <textarea
                  name="notes"
                  value={formData.notes}
                  onChange={handleInputChange}
                  rows="3"
                  placeholder="Additional notes about this lead..."
                />
              </div>
            </div>
          </div>

          {/* Contact Persons */}
          <div className="form-section">
            <div className="contact-header">
              <h4>Contact Persons</h4>
              <ButtonWithIcon
                type="button"
                icon="mdi:account-plus"
                text="Add Contact"
                onClick={addContactPerson}
                className="btn-secondary btn-sm"
              />
            </div>

            {formData.contactPerson.map((contact, index) => (
              <div key={index} className="contact-group">
                <div className="contact-person-header">
                  {formData.contactPerson.length > 1 && (
                    <button
                      type="button"
                      className="remove-contact-btn"
                      onClick={() => removeContactPerson(index)}
                      title="Remove contact person"
                    >
                      ×
                    </button>
                  )}
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Name</label>
                    <input
                      type="text"
                      placeholder="Contact person name"
                      value={contact.contactPersonName}
                      onChange={(e) =>
                        updateContactPerson(
                          index,
                          "contactPersonName",
                          e.target.value,
                        )
                      }
                    />
                  </div>
                  <div className="form-group">
                    <label>Email</label>
                    <input
                      type="email"
                      placeholder="contact@example.com"
                      value={contact.email}
                      onChange={(e) =>
                        updateContactPerson(index, "email", e.target.value)
                      }
                    />
                  </div>
                  <div className="form-group">
                    <label>Mobile Number</label>
                    <input
                      type="tel"
                      placeholder="Mobile number"
                      value={contact.mobileNumber}
                      onChange={(e) =>
                        updateContactPerson(
                          index,
                          "mobileNumber",
                          e.target.value.replace(/\D/g, ""),
                        )
                      }
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>WhatsApp Number</label>
                    <input
                      type="tel"
                      placeholder="WhatsApp number"
                      value={contact.whatsAppNumber}
                      onChange={(e) =>
                        updateContactPerson(
                          index,
                          "whatsAppNumber",
                          e.target.value.replace(/\D/g, ""),
                        )
                      }
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Footer */}
          <div className="modal-footer">
            <ButtonWithIcon
              type="button"
              icon="mdi:cancel"
              text="Cancel"
              onClick={onClose}
              className="cancel-button"
            />
            <ButtonWithIcon
              type="submit"
              icon={loading ? "mdi:loading" : "mdi:check"}
              text={loading ? "Creating..." : "Create Lead"}
              disabled={loading}
              className="submit-button"
            />
          </div>
        </form>
      </div>
    </div>
  );
};

export default LeadQuickAddModal;
